// frontend/src/pages/admin/User.tsx
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  HStack,
  Select,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Stack,
  VStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  NumberInput,
  NumberInputField,
  ButtonGroup,
  FormControl,
  FormLabel,
  InputRightElement,
  IconButton,
  Textarea,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import { SearchIcon, ViewIcon, ViewOffIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  getUserList,
  adjustUserUsdt,
  adjustUserPoint,
  adminChangePassword,
  adminChangeUsername,
  purchaseUserPlan,
  increaseService as apiIncreaseService,
  createUserByAdmin,
  setUserServiceDate,
  adminSetApiKey,
  adminGetApiKey,
  adminSetIsAdmin,
  adminGetPosition,
  adminClosePosition,
  adminCloseAllPositions,
  adminSwitchMarginMode,
  adminSetReferrer,
  adminGetReferrer,
  deleteUserByAdmin,
  adminSetMemo,
  adminGetMemo,
  adminResetDCA,
  adminToggleBotStatus,
  adminToggleAllBotStatus
} from '../../services/apiService'
import dayjs from 'dayjs'

type UserItem = {
  id: number
  account: string
  username: string
  email: string | null
  phone: string | null
  usdtBalance: string | null
  pointBalance: string | null
  isAdmin: number
  serviceAt: string | null
  referralAt: string | null
  subscriptionAt: string | null
  adminMemo: string | null

  createdAt: string | null
  trading: {
    symbol: string
    botStatus: number
    size: number
    currentAddOrderLong: number
    maxAddOrderLong: number
    currentAddOrderShort: number
    maxAddOrderShort: number
    seedAmount: number | null
    strategyRatio: string | null
  }[]
}

// 라벨/코드 분리 (서버에는 product 코드만 보냄)
const PLAN_OPTIONS = [
  { label: '체험판 (7일 제공)', value: 'DEMO' },
  { label: 'BASIC 플랜 [-300 USDT]', value: 'BASIC' },
  { label: 'VIP 플랜 [-3,600 USDT]', value: 'VIP' },
  { label: 'SECRET 플랜 [-5,000 USDT]', value: 'SECRET' },
  { label: 'SECRET+ 플랜 [-7,000 USDT]', value: 'SECRET+' }
] as const
type PlanValue = (typeof PLAN_OPTIONS)[number]['value']

const User = () => {
  const { t } = useTranslation()
  const toast = useToast()

  const [keyword, setKeyword] = useState('')
  const [memoKeyword, setMemoKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<UserItem[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const toggleRowExpand = (userId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }
      return newSet
    })
  }

  // 액션 대상 유저
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null)

  // USDT 조정
  const adjustDisc = useDisclosure()
  const [adjustAmount, setAdjustAmount] = useState<string>('0')
  const [adjustType, setAdjustType] = useState<'credit' | 'debit'>('credit')
  const [submittingAdjust, setSubmittingAdjust] = useState(false)

  // 포인트 조정
  const adjustPointDisc = useDisclosure()
  const [adjustPointAmount, setAdjustPointAmount] = useState<string>('0')
  const [adjustPointType, setAdjustPointType] = useState<'credit' | 'debit'>('credit')
  const [submittingAdjustPoint, setSubmittingAdjustPoint] = useState(false)

  // 비밀번호 변경
  const changePasswordDisc = useDisclosure()
  const [newPassword, setNewPassword] = useState<string>('')
  const [secretKey, setSecretKey] = useState<string>('')
  const [submittingChangePassword, setSubmittingChangePassword] = useState(false)

  // 사용자명 변경
  const changeUsernameDisc = useDisclosure()
  const [newUsername, setNewUsername] = useState<string>('')
  const [submittingChangeUsername, setSubmittingChangeUsername] = useState(false)

  // 이용권 구매
  const planDisc = useDisclosure()
  const [selectedPlan, setSelectedPlan] = useState<PlanValue>('DEMO')
  const [submittingPlan, setSubmittingPlan] = useState(false)

  // ✅ 이용일 증가(관리자)
  const increaseDisc = useDisclosure()
  const [increaseDays, setIncreaseDays] = useState<string>('1')
  const [nextExpiryHint, setNextExpiryHint] = useState<string>('')
  const [submittingIncrease, setSubmittingIncrease] = useState(false)

  // ✅ 날짜 조정(관리자)
  const setDateDisc = useDisclosure()
  const [dateType, setDateType] = useState<'serviceAt' | 'referralAt' | 'subscriptionAt'>('serviceAt')
  const [newDateValue, setNewDateValue] = useState<string>('')
  const [submittingSetDate, setSubmittingSetDate] = useState(false)

  // API 키 설정
  const apiKeyDisc = useDisclosure()
  const [apiKeyValue, setApiKeyValue] = useState<string>('')
  const [apiSecretValue, setApiSecretValue] = useState<string>('')
  const [showApiSecret, setShowApiSecret] = useState(false)
  const [submittingApiKey, setSubmittingApiKey] = useState(false)

  // 포지션 청산
  const positionDisc = useDisclosure()
  const [positionData, setPositionData] = useState<any>(null)
  const [loadingPosition, setLoadingPosition] = useState(false)
  const [submittingClosePosition, setSubmittingClosePosition] = useState(false)
  const [resettingDCA, setResettingDCA] = useState(false)
  const [closeSizeLong, setCloseSizeLong] = useState<string>('')
  const [closeSizeShort, setCloseSizeShort] = useState<string>('')

  // 마진 모드 변경 (별도 모달)
  const marginModeDisc = useDisclosure()
  const [loadingMarginCheck, setLoadingMarginCheck] = useState(false)
  const [submittingMarginMode, setSubmittingMarginMode] = useState(false)
  const [canChangeMarginMode, setCanChangeMarginMode] = useState(false)

  // 추천인 설정
  const referrerDisc = useDisclosure()
  const [referrerAccount, setReferrerAccount] = useState<string>('')
  const [submittingReferrer, setSubmittingReferrer] = useState(false)

  // 회원 추가
  const createUserDisc = useDisclosure()
  const [newUserAccount, setNewUserAccount] = useState<string>('')
  const [newUserPassword, setNewUserPassword] = useState<string>('')
  const [newUserUsername, setNewUserUsername] = useState<string>('')
  const [newUserPhone, setNewUserPhone] = useState<string>('')
  const [newUserReferrer, setNewUserReferrer] = useState<string>('')
  const [submittingCreateUser, setSubmittingCreateUser] = useState(false)

  // 관리자 메모
  const memoDisc = useDisclosure()
  const [memoContent, setMemoContent] = useState<string>('')
  const [submittingMemo, setSubmittingMemo] = useState(false)

  // DCA 초기화 모달
  const dcaResetDisc = useDisclosure()
  const [dcaResetData, setDcaResetData] = useState<{ long: number; short: number } | null>(null)
  const [loadingDcaData, setLoadingDcaData] = useState(false)

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  // XRP 심볼의 trading 레코드 찾기
  const getXrpTrading = (user: UserItem) => {
    return user.trading?.find(t => t.symbol === 'XRP') || user.trading?.[0]
  }

  // 입력 변화나 선택 유저 변화 시 예상 만료일 계산
  useEffect(() => {
    if (!selectedUser) {
      setNextExpiryHint('')
      return
    }

    const days = Number(increaseDays)
    if (!Number.isFinite(days) || days <= 0) {
      setNextExpiryHint('')
      return
    }

    const now = dayjs()
    const current = selectedUser.serviceAt
      ? dayjs(selectedUser.serviceAt)
      : null

    const base = current && current.isAfter(now) ? current : now
    const newDate = base.add(days, 'day').format('YYYY-MM-DD HH:mm:ss')

    setNextExpiryHint(`연장 후 만료일: ${newDate}`)
  }, [increaseDays, selectedUser])

  const isMobile = useBreakpointValue({ base: true, md: false })

  const fetchList = async (
    override?: Partial<{ page: number; pageSize: number; keyword: string }>
  ) => {
    try {
      setLoading(true)
      const params = { page, pageSize, keyword, memoKeyword, ...override }
      const res = await getUserList(params)
      const payload = res?.data?.data
      setItems(payload?.items ?? [])
      setTotal(payload?.total ?? 0)
      if (typeof payload?.page === 'number') setPage(payload.page)
      if (typeof payload?.pageSize === 'number') setPageSize(payload.pageSize)
    } catch (e: any) {
      toast({
        title: '회원 목록 조회 실패',
        description: e?.message ?? '네트워크 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const onSearch = () => {
    setPage(1)
    fetchList({ page: 1 })
  }

  const formatDate = (v?: string | null) => {
    if (!v) return '-'
    const d = dayjs(v)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : v
  }

  const ExpiryCell = ({ value }: { value?: string | null }) => {
    if (!value) return <Text>-</Text>
    const d = dayjs(value)
    if (!d.isValid()) return <Text>{value}</Text>
    const now = dayjs()
    const diffDays = d.endOf('day').diff(now, 'day')
    const isExpired = d.isBefore(now, 'minute')
    const label = isExpired
      ? `${Math.abs(diffDays)}일 지남`
      : `${diffDays}일 남음`
    const display = d.format('YYYY-MM-DD HH:mm:ss')
    return (
      <Tooltip label={label} openDelay={300}>
        <Text color={isExpired ? 'red.500' : undefined}>{display}</Text>
      </Tooltip>
    )
  }

  const formatUSDTInt = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const intPart = String(v).split('.')[0] || '0'
    const num = Number(intPart.replace(/[^0-9-]/g, ''))
    if (Number.isNaN(num)) return intPart
    return num.toLocaleString()
  }

  // === 모달 열기 ===
  const openAdjustModal = (user: UserItem) => {
    setSelectedUser(user)
    setAdjustAmount('0')
    setAdjustType('credit')
    adjustDisc.onOpen()
  }

  const openAdjustPointModal = (user: UserItem) => {
    setSelectedUser(user)
    setAdjustPointAmount('0')
    setAdjustPointType('credit')
    adjustPointDisc.onOpen()
  }

  const openChangePasswordModal = (user: UserItem) => {
    setSelectedUser(user)
    setNewPassword('')
    setSecretKey('')
    changePasswordDisc.onOpen()
  }

  const openChangeUsernameModal = (user: UserItem) => {
    setSelectedUser(user)
    setNewUsername(user.username || '')
    changeUsernameDisc.onOpen()
  }

  const openPlanModal = (user: UserItem) => {
    setSelectedUser(user)
    setSelectedPlan('DEMO')
    planDisc.onOpen()
  }

  const openIncreaseModal = (user: UserItem) => {
    setSelectedUser(user)
    setIncreaseDays('1')
    increaseDisc.onOpen()
  }

  const openSetDateModal = (user: UserItem) => {
    setSelectedUser(user)
    setDateType('serviceAt')
    setNewDateValue('')
    setDateDisc.onOpen()
  }

  const openApiKeyModal = async (user: UserItem) => {
    setSelectedUser(user)
    setApiKeyValue('')
    setApiSecretValue('')
    apiKeyDisc.onOpen()
    
    // 기존 API 키 조회
    try {
      const res = await adminGetApiKey(user.id)
      if (res?.data?.data) {
        setApiKeyValue(res.data.data.apiKey || '')
        setApiSecretValue(res.data.data.apiSecret || '')
      }
    } catch (e: any) {
      // 조회 실패시 빈 값 유지 (새로 설정하는 것으로 간주)
      console.error('API 키 조회 실패:', e)
    }
  }

  // 포지션 청산 모달 열기
  const openPositionModal = async (user: UserItem) => {
    setSelectedUser(user)
    setPositionData(null)
    setLoadingPosition(true)
    setCloseSizeLong('')
    setCloseSizeShort('')
    positionDisc.onOpen()
    
    try {
      const res = await adminGetPosition(user.id, 'XRP')
      if (res?.data?.data) {
        setPositionData(res.data.data)
      }
    } catch (e: any) {
      toast({
        title: '포지션 조회 실패',
        description: e?.response?.data?.message ?? e.message ?? '포지션 정보를 조회할 수 없습니다.',
        status: 'error'
      })
      setPositionData(null)
    } finally {
      setLoadingPosition(false)
    }
  }

  // 포지션 청산 실행 (size: 부분 청산 사이즈, undefined면 전체 청산)
  const submitClosePosition = async (side: 'long' | 'short' | 'all', size?: number) => {
    if (!selectedUser) return

    const sideLabel = side === 'long' ? '롱' : side === 'short' ? '숏' : '전체'
    const sizeLabel = size ? `${size} 계약` : '전체'
    if (!window.confirm(`정말 ${sideLabel} 포지션을 ${sizeLabel} 청산하시겠습니까?`)) return

    try {
      setSubmittingClosePosition(true)
      const { data: res } = await adminClosePosition({
        userId: selectedUser.id,
        symbol: 'XRP',
        side,
        size: size || undefined
      })
      if (res.statusCode === 200) {
        toast({
          title: `${sideLabel} 포지션 ${sizeLabel} 청산 완료`,
          description: res.message,
          status: 'success'
        })
        // 사이즈 입력 초기화
        if (side === 'long' || side === 'all') setCloseSizeLong('')
        if (side === 'short' || side === 'all') setCloseSizeShort('')
        // 포지션 갱신
        openPositionModal(selectedUser)
      }
    } catch (e: any) {
      toast({
        title: '포지션 청산 실패',
        description: e?.response?.data?.message ?? e.message ?? '청산 처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingClosePosition(false)
    }
  }

  // 물타기 횟수 초기화
  const handleResetDCA = async (side: 'long' | 'short' | 'all') => {
    if (!selectedUser) return

    const sideLabel = side === 'long' ? '롱' : side === 'short' ? '숏' : '전체'
    if (!window.confirm(`정말 ${sideLabel} 물타기 횟수를 초기화하시겠습니까?`)) return

    try {
      setResettingDCA(true)
      const { data: res } = await adminResetDCA({
        userId: selectedUser.id,
        symbol: 'XRP',
        side
      })
      if (res.statusCode === 200) {
        toast({
          title: `${sideLabel} 물타기 횟수 초기화 완료`,
          description: res.message,
          status: 'success'
        })
        // 포지션 갱신
        openPositionModal(selectedUser)
      }
    } catch (e: any) {
      toast({
        title: '물타기 횟수 초기화 실패',
        description: e?.response?.data?.message ?? e.message ?? '초기화 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setResettingDCA(false)
    }
  }

  // DCA 초기화 모달 열기
  const openDcaResetModal = async (user: UserItem) => {
    setSelectedUser(user)
    setDcaResetData(null)
    setLoadingDcaData(true)
    dcaResetDisc.onOpen()
    try {
      const res = await adminGetPosition(user.id, 'XRP')
      if (res?.data?.data) {
        setDcaResetData({
          long: res.data.data.currentAddOrderLong || 0,
          short: res.data.data.currentAddOrderShort || 0
        })
      }
    } catch {
      setDcaResetData({ long: 0, short: 0 })
    } finally {
      setLoadingDcaData(false)
    }
  }

  // DCA 모달에서 리셋 후 데이터 새로고침
  const handleDcaReset = async (side: 'long' | 'short' | 'all') => {
    if (!selectedUser) return

    const sideLabel = side === 'long' ? '롱' : side === 'short' ? '숏' : '전체'
    if (!window.confirm(`정말 ${sideLabel} 물타기 횟수를 초기화하시겠습니까?`)) return

    try {
      setResettingDCA(true)
      const { data: res } = await adminResetDCA({
        userId: selectedUser.id,
        symbol: 'XRP',
        side
      })
      if (res.statusCode === 200) {
        toast({
          title: `${sideLabel} 물타기 횟수 초기화 완료`,
          description: res.message,
          status: 'success'
        })
        // DCA 데이터 갱신
        setDcaResetData({
          long: res.data?.currentAddOrderLong ?? 0,
          short: res.data?.currentAddOrderShort ?? 0
        })
      }
    } catch (e: any) {
      toast({
        title: '물타기 횟수 초기화 실패',
        description: e?.response?.data?.message ?? e.message ?? '초기화 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setResettingDCA(false)
    }
  }

  // 마진 모드 모달 열기 (포지션 확인)
  const openMarginModeModal = async (user: UserItem) => {
    setSelectedUser(user)
    setCanChangeMarginMode(false)
    setLoadingMarginCheck(true)
    marginModeDisc.onOpen()
    try {
      const res = await adminGetPosition(user.id, 'XRP')
      if (res?.data?.data) {
        setCanChangeMarginMode(!res.data.data.hasPosition)
      }
    } catch (e: any) {
      toast({ title: '포지션 확인 실패', description: e?.response?.data?.message ?? '포지션 정보를 조회할 수 없습니다.', status: 'error' })
      setCanChangeMarginMode(false)
    } finally {
      setLoadingMarginCheck(false)
    }
  }

  // 마진 모드 변경 실행
  const submitSwitchMarginMode = async (mode: 'CROSS' | 'ISOLATED') => {
    if (!selectedUser) return
    const modeLabel = mode === 'CROSS' ? '교차(Cross)' : '격리(Isolated)'
    if (!window.confirm(`${modeLabel} 마진 모드로 변경하시겠습니까?`)) return

    try {
      setSubmittingMarginMode(true)
      const { data: res } = await adminSwitchMarginMode({ userId: selectedUser.id, symbol: 'XRP', mode })
      if (res.statusCode === 200) {
        toast({ title: '마진 모드 변경 완료', description: res.message, status: 'success' })
        marginModeDisc.onClose()
      }
    } catch (e: any) {
      toast({ title: '마진 모드 변경 실패', description: e?.response?.data?.message ?? '변경 중 오류가 발생했습니다.', status: 'error' })
    } finally {
      setSubmittingMarginMode(false)
    }
  }

  // 자동매매 ON/OFF 토글
  const handleToggleBotStatus = async (user: UserItem, newStatus: boolean) => {
    const statusLabel = newStatus ? 'ON' : 'OFF'
    const symbol = 'XRP' // 항상 XRP 심볼 사용
    if (!window.confirm(`${user.account}님의 자동매매를 ${statusLabel}으로 변경하시겠습니까?`)) return

    try {
      const { data: res } = await adminToggleBotStatus({
        userId: user.id,
        symbol,
        botStatus: newStatus
      })
      if (res.statusCode === 200) {
        toast({ title: `자동매매 ${statusLabel}`, description: res.message, status: 'success' })
        fetchList()
      }
    } catch (e: any) {
      toast({ title: '자동매매 변경 실패', description: e?.response?.data?.message ?? '변경 중 오류가 발생했습니다.', status: 'error' })
    }
  }

  // 전체 자동매매 ON/OFF 토글
  const handleToggleAllBotStatus = async (newStatus: boolean) => {
    const statusLabel = newStatus ? 'ON' : 'OFF'
    if (!window.confirm(`전체 사용자의 자동매매를 ${statusLabel}으로 변경하시겠습니까?`)) return

    try {
      const { data: res } = await adminToggleAllBotStatus({
        symbol: 'XRP',
        botStatus: newStatus
      })
      if (res.statusCode === 200) {
        toast({ title: `전체 자동매매 ${statusLabel}`, description: res.message, status: 'success' })
        fetchList()
      }
    } catch (e: any) {
      toast({ title: '전체 자동매매 변경 실패', description: e?.response?.data?.message ?? '변경 중 오류가 발생했습니다.', status: 'error' })
    }
  }

  // === 제출: API 키 설정 ===
  const submitApiKey = async () => {
    if (!selectedUser) return
    
    if (!apiKeyValue.trim() || !apiSecretValue.trim()) {
      toast({ title: 'API 키와 시크릿을 모두 입력하세요.', status: 'warning' })
      return
    }

    try {
      setSubmittingApiKey(true)
      const { data: res } = await adminSetApiKey({
        userId: selectedUser.id,
        apiKey: apiKeyValue.trim(),
        apiSecret: apiSecretValue.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: 'API 키 설정 완료',
          description: res.message,
          status: 'success'
        })
        apiKeyDisc.onClose()
        setApiKeyValue('')
        setApiSecretValue('')
      }
    } catch (e: any) {
      toast({
        title: 'API 키 설정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingApiKey(false)
    }
  }

  // === 제출: USDT 조정 ===
  const submitAdjust = async () => {
    if (!selectedUser) return
    const raw = Number(adjustAmount)
    if (Number.isNaN(raw)) {
      toast({ title: '숫자를 입력하세요.', status: 'warning' })
      return
    }
    const signedAmount = adjustType === 'credit' ? raw : -raw
    if (signedAmount === 0) {
      toast({ title: '0은 입력할 수 없습니다.', status: 'warning' })
      return
    }

    try {
      setSubmittingAdjust(true)
      const { data: res } = await adjustUserUsdt({
        userId: selectedUser.id,
        amount: signedAmount
      })
      if (res.statusCode === 200) {
        toast({
          title: 'USDT 조정 완료',
          description: res.message,
          status: 'success'
        })
        adjustDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: 'USDT 조정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingAdjust(false)
    }
  }

  // === 제출: 포인트 조정 ===
  const submitAdjustPoint = async () => {
    if (!selectedUser) return
    const raw = Number(adjustPointAmount)
    if (Number.isNaN(raw)) {
      toast({ title: '숫자를 입력하세요.', status: 'warning' })
      return
    }
    const signedAmount = adjustPointType === 'credit' ? raw : -raw
    if (signedAmount === 0) {
      toast({ title: '0은 입력할 수 없습니다.', status: 'warning' })
      return
    }

    try {
      setSubmittingAdjustPoint(true)
      const { data: res } = await adjustUserPoint({
        userId: selectedUser.id,
        amount: signedAmount
      })
      if (res.statusCode === 200) {
        toast({
          title: '포인트 조정 완료',
          description: res.message,
          status: 'success'
        })
        adjustPointDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '포인트 조정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingAdjustPoint(false)
    }
  }

  // === 제출: 비밀번호 변경 ===
  const submitChangePassword = async () => {
    if (!selectedUser) return
    
    if (!newPassword.trim()) {
      toast({ title: '새 비밀번호를 입력하세요.', status: 'warning' })
      return
    }
    
    if (newPassword.length < 4) {
      toast({ title: '비밀번호는 4글자 이상이어야 합니다.', status: 'warning' })
      return
    }
    
    if (!secretKey.trim()) {
      toast({ title: '비밀키를 입력하세요.', status: 'warning' })
      return
    }

    try {
      setSubmittingChangePassword(true)
      const { data: res } = await adminChangePassword({
        userId: selectedUser.id,
        newPassword: newPassword.trim(),
        secretKey: secretKey.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: '비밀번호 변경 완료',
          description: res.message,
          status: 'success'
        })
        changePasswordDisc.onClose()
        setNewPassword('')
        setSecretKey('')
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '비밀번호 변경 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingChangePassword(false)
    }
  }

  // === 제출: 사용자명 변경 ===
  const submitChangeUsername = async () => {
    if (!selectedUser) return
    
    if (!newUsername.trim()) {
      toast({ title: '새 사용자명을 입력하세요.', status: 'warning' })
      return
    }

    try {
      setSubmittingChangeUsername(true)
      const { data: res } = await adminChangeUsername({
        userId: selectedUser.id,
        newUsername: newUsername.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: '사용자명 변경 완료',
          description: res.message,
          status: 'success'
        })
        changeUsernameDisc.onClose()
        setNewUsername('')
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '사용자명 변경 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingChangeUsername(false)
    }
  }

  // === 제출: 이용권 구매 ===
  const submitPlan = async () => {
    if (!selectedUser) return
    try {
      setSubmittingPlan(true)
      const { data: res } = await purchaseUserPlan({
        userId: selectedUser.id,
        product: selectedPlan
      })
      if (res.statusCode === 200) {
        toast({ title: '이용권 구매 완료', status: 'success' })
        planDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '이용권 구매 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingPlan(false)
    }
  }

  // ✅ 제출: 이용일 증가(관리자)
  const submitIncrease = async () => {
    if (!selectedUser) return
    const days = Number(increaseDays)
    if (!Number.isFinite(days) || days <= 0) {
      toast({ title: '증가 일수를 올바르게 입력하세요.', status: 'warning' })
      return
    }
    try {
      setSubmittingIncrease(true)
      const { data: res } = await apiIncreaseService({
        userId: selectedUser.id,
        addDay: days
      })
      if (res.statusCode === 200) {
        toast({
          title: '이용일 증가 완료',
          description: res.message,
          status: 'success'
        })
        increaseDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '이용일 증가 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingIncrease(false)
    }
  }

  // === 제출: 날짜 조정 ===
  const submitSetDate = async () => {
    if (!selectedUser) return
    if (!newDateValue) {
      toast({ title: '날짜를 선택하세요.', status: 'warning' })
      return
    }
    try {
      setSubmittingSetDate(true)
      const { data: res } = await setUserServiceDate({
        userId: selectedUser.id,
        dateType: dateType,
        newDate: new Date(newDateValue).toISOString()
      })
      if (res.statusCode === 200) {
        toast({
          title: '날짜 조정 완료',
          description: res.message,
          status: 'success'
        })
        setDateDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '날짜 조정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingSetDate(false)
    }
  }

  // ✅ 관리자 권한 토글
  const handleToggleAdmin = async (u: UserItem) => {
    const newValue = u.isAdmin ? 0 : 1
    const action = newValue === 1 ? '관리자 권한 부여' : '관리자 권한 해제'
    
    if (!window.confirm(`${u.username}님의 ${action}를 하시겠습니까?`)) return
    
    try {
      const { data: res } = await adminSetIsAdmin({
        targetUserId: u.id,
        isAdmin: newValue
      })
      if (res.statusCode === 200) {
        toast({
          title: action + ' 완료',
          description: res.message,
          status: 'success'
        })
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: action + ' 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    }
  }

  // === 추천인 설정 ===
  const openReferrerModal = async (user: UserItem) => {
    setSelectedUser(user)
    setReferrerAccount('')
    referrerDisc.onOpen()

    // 기존 추천인 조회
    try {
      const res = await adminGetReferrer(user.id)
      if (res?.data?.data?.referrerAccount) {
        setReferrerAccount(res.data.data.referrerAccount)
      }
    } catch (e: any) {
      console.error('추천인 조회 실패:', e)
    }
  }

  const submitReferrer = async () => {
    if (!selectedUser) return

    try {
      setSubmittingReferrer(true)
      const { data: res } = await adminSetReferrer({
        userId: selectedUser.id,
        referrerAccount: referrerAccount.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: '추천인 설정 완료',
          description: res.message,
          status: 'success'
        })
        referrerDisc.onClose()
        setReferrerAccount('')
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '추천인 설정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingReferrer(false)
    }
  }

  // === 회원 추가 ===
  const openCreateUserModal = () => {
    setNewUserAccount('')
    setNewUserPassword('')
    setNewUserUsername('')
    setNewUserPhone('')
    setNewUserReferrer('')
    createUserDisc.onOpen()
  }

  const submitCreateUser = async () => {
    if (!newUserAccount.trim()) {
      toast({ title: '아이디를 입력하세요.', status: 'warning' })
      return
    }
    if (!newUserPassword.trim()) {
      toast({ title: '비밀번호를 입력하세요.', status: 'warning' })
      return
    }
    if (!newUserUsername.trim()) {
      toast({ title: '사용자명을 입력하세요.', status: 'warning' })
      return
    }
    if (!newUserPhone.trim()) {
      toast({ title: '연락처를 입력하세요.', status: 'warning' })
      return
    }

    try {
      setSubmittingCreateUser(true)
      const { data: res } = await createUserByAdmin({
        account: newUserAccount.trim(),
        password: newUserPassword.trim(),
        username: newUserUsername.trim(),
        phone: newUserPhone.trim(),
        referrer: newUserReferrer.trim() || undefined
      })
      if (res.statusCode === 200) {
        toast({
          title: '회원 추가 완료',
          description: res.message,
          status: 'success'
        })
        createUserDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '회원 추가 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingCreateUser(false)
    }
  }

  // ✅ 전체 유저 포지션 청산
  const [closingAllPositions, setClosingAllPositions] = useState(false)
  const handleCloseAllPositions = async (side: 'long' | 'short' | 'all') => {
    const sideLabel = side === 'long' ? '롱' : side === 'short' ? '숏' : '모든'
    if (!window.confirm(`정말 모든 유저의 XRP ${sideLabel} 포지션을 청산하시겠습니까?\n\n이 작업은 모든 유저의 XRP ${sideLabel} 포지션을 일괄 청산합니다.`)) return

    try {
      setClosingAllPositions(true)
      const { data: res } = await adminCloseAllPositions(side)
      toast({
        title: `${sideLabel} 포지션 전체 청산 완료`,
        description: res.message,
        status: 'success',
        duration: 8000
      })
    } catch (e: any) {
      toast({
        title: `${sideLabel} 포지션 전체 청산 실패`,
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setClosingAllPositions(false)
    }
  }

  // === 회원 삭제 ===
  const handleDeleteUser = async (user: UserItem) => {
    if (!window.confirm(`정말 ${user.username}(${user.account}) 회원을 삭제하시겠습니까?\n\n삭제된 회원은 로그인할 수 없습니다.`)) return

    try {
      const { data: res } = await deleteUserByAdmin(user.id)
      if (res.statusCode === 200) {
        toast({
          title: '회원 삭제 완료',
          description: res.message,
          status: 'success'
        })
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '회원 삭제 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    }
  }

  // === 관리자 메모 ===
  const openMemoModal = async (user: UserItem) => {
    setSelectedUser(user)
    setMemoContent('')
    memoDisc.onOpen()

    // 기존 메모 조회
    try {
      const res = await adminGetMemo(user.id)
      if (res?.data?.data?.memo) {
        setMemoContent(res.data.data.memo)
      }
    } catch (e: any) {
      console.error('메모 조회 실패:', e)
    }
  }

  const submitMemo = async () => {
    if (!selectedUser) return

    try {
      setSubmittingMemo(true)
      const { data: res } = await adminSetMemo({
        userId: selectedUser.id,
        memo: memoContent
      })
      if (res.statusCode === 200) {
        toast({
          title: '메모 저장 완료',
          description: res.message,
          status: 'success'
        })
        memoDisc.onClose()
      }
    } catch (e: any) {
      toast({
        title: '메모 저장 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingMemo(false)
    }
  }

  // === 모바일 카드 UI ===
  const MobileCard = ({ u }: { u: UserItem }) => (
    <Box borderWidth="1px" borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontWeight="bold">{u.username}</Text>
        {u.isAdmin ? (
          <Badge colorScheme="cyan" variant="subtle">
            관리자
          </Badge>
        ) : (
          <Badge colorScheme="gray" variant="subtle">
            일반
          </Badge>
        )}
      </Flex>
      <Text fontSize="sm" color="gray.400">
        @{u.account}
      </Text>
      <Divider my={3} />

      <Stack spacing={1} fontSize="sm">
        <Flex justify="space-between">
          <Text color="gray.500">연락처</Text>
          <Text>{u.phone ?? '-'}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">보유 USDT</Text>
          <Text fontWeight="semibold">{formatUSDTInt(u.usdtBalance)}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">보유 포인트</Text>
          <Text fontWeight="semibold" color="blue.600">{formatUSDTInt(u.pointBalance)} P</Text>
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">서비스일</Text>
          <ExpiryCell value={u.serviceAt} />
        </Flex>
        <Flex justify="space-between" align="start">
          <Text color="gray.500">봇 상태</Text>
          {(() => {
            const trading = u.trading?.find(t => t.symbol === "XRP") || u.trading?.[0]
            if(!trading) return <Text fontSize="xs" color="gray.500">-</Text>
            
            return (
              <VStack align="end" spacing={1} fontSize="xs">
                <Badge colorScheme="purple" variant="subtle" px={1}>
                  SIZE: {trading.size}
                </Badge>
                <HStack spacing={1}>
                  <Badge colorScheme="blue" variant="outline" px={1}>
                    L: {trading.currentAddOrderLong}/{trading.maxAddOrderLong}
                  </Badge>
                  <Badge colorScheme="red" variant="outline" px={1}>
                    S: {trading.currentAddOrderShort}/{trading.maxAddOrderShort}
                  </Badge>
                </HStack>
              </VStack>
            )
          })()}
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">레퍼럴</Text>
          <ExpiryCell value={u.referralAt} />
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">구독료</Text>
          <ExpiryCell value={u.subscriptionAt} />
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">생성일</Text>
          <Text>{formatDate(u.createdAt)}</Text>
        </Flex>
        {u.adminMemo && (
          <Box mt={2} p={2} bg="yellow.900" borderRadius="md">
            <Text fontSize="xs" color="yellow.400" fontWeight="bold" mb={1}>📝 메모</Text>
            <Text fontSize="xs" color="yellow.200" whiteSpace="pre-wrap">{u.adminMemo}</Text>
          </Box>
        )}
      </Stack>

      <HStack mt={3} spacing={2} wrap="wrap">
        <Button size="sm" variant="outline" onClick={() => openAdjustModal(u)}>
          USDT 조정
        </Button>
        <Button size="sm" variant="outline" colorScheme="blue" onClick={() => openAdjustPointModal(u)}>
          포인트 조정
        </Button>
        <Button size="sm" variant="outline" colorScheme="cyan" onClick={() => openChangeUsernameModal(u)}>
          사용자명변경
        </Button>
        <Button size="sm" variant="outline" colorScheme="red" onClick={() => openChangePasswordModal(u)}>
          비밀번호변경
        </Button>
        <Button size="sm" colorScheme="green" onClick={() => openPlanModal(u)}>
          이용권 구매
        </Button>
        {/* ✅ 이용일 증가 버튼 */}
        <Button
          size="sm"
          colorScheme="teal"
          onClick={() => openIncreaseModal(u)}
        >
          이용일 증가
        </Button>
        {/* ✅ 날짜 조정 버튼 */}
        <Button
          size="sm"
          colorScheme="purple"
          onClick={() => openSetDateModal(u)}
        >
          날짜 조정
        </Button>
        <Button
          size="sm"
          colorScheme="orange"
          onClick={() => openApiKeyModal(u)}
        >
          API 설정
        </Button>
        <Button
          size="sm"
          colorScheme="teal"
          onClick={() => openReferrerModal(u)}
        >
          추천인
        </Button>
        {/* ✅ 포지션 청산 버튼 */}
        <Button
          size="sm"
          colorScheme="pink"
          onClick={() => openPositionModal(u)}
        >
          포지션 청산
        </Button>
        {/* ✅ 자동매매 ON/OFF 버튼 */}
        <Button
          size="sm"
          colorScheme={getXrpTrading(u)?.botStatus ? 'red' : 'green'}
          variant="solid"
          onClick={() => handleToggleBotStatus(u, !getXrpTrading(u)?.botStatus)}
        >
          {getXrpTrading(u)?.botStatus ? '봇OFF' : '봇ON'}
        </Button>
        {/* ✅ 마진 모드 버튼 */}
        <Button
          size="sm"
          colorScheme="cyan"
          onClick={() => openMarginModeModal(u)}
        >
          마진
        </Button>
        {/* ✅ 회원 삭제 버튼 */}
        {!u.isAdmin && (
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            onClick={() => handleDeleteUser(u)}
          >
            삭제
          </Button>
        )}
        {/* ✅ 메모 버튼 */}
        <Button
          size="sm"
          colorScheme="yellow"
          variant="outline"
          onClick={() => openMemoModal(u)}
        >
          메모
        </Button>
      </HStack>
    </Box>
  )

  return (
    <DefaultLayout>
      <AdminNav />

      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* 검색/필터 */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          mb={3}
          gap={3}
        >
          <Heading as="h2" size="md" color="white">
            회원 관리
          </Heading>

          <HStack spacing={3} align="stretch">
            <InputGroup width={{ base: '100%', md: '280px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="아이디/이름/연락처"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                bg="dark.bg"
                borderColor="whiteAlpha.300"
                color="white"
                _placeholder={{ color: 'gray.500' }}
                _hover={{ borderColor: 'whiteAlpha.400' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
              />
            </InputGroup>
            <InputGroup width={{ base: '100%', md: '180px' }}>
              <InputLeftElement pointerEvents="none">
                <Text color="gray.500" fontSize="xs">메모</Text>
              </InputLeftElement>
              <Input
                placeholder="메모 검색"
                value={memoKeyword}
                onChange={(e) => setMemoKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                bg="dark.bg"
                borderColor="whiteAlpha.300"
                color="white"
                pl={10}
                _placeholder={{ color: 'gray.500' }}
                _hover={{ borderColor: 'whiteAlpha.400' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
              />
            </InputGroup>
            <Button 
              onClick={onSearch} 
              minW={{ base: 'auto', md: '70px' }}
              colorScheme="cyan"
              bgGradient="linear(to-r, cyan.500, blue.500)"
              color="white"
              _hover={{ 
                bgGradient: "linear(to-r, cyan.400, blue.400)",
                boxShadow: "0 0 10px rgba(0, 200, 255, 0.4)"
              }}
            >
              검색
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              onClick={openCreateUserModal}
            >
              회원 추가
            </Button>
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                colorScheme="red"
                rightIcon={<ChevronDownIcon />}
                isLoading={closingAllPositions}
              >
                전체 청산
              </MenuButton>
              <MenuList bg="gray.800" borderColor="gray.600">
                <MenuItem
                  bg="gray.800"
                  _hover={{ bg: 'gray.700' }}
                  onClick={() => handleCloseAllPositions('short')}
                >
                  🔴 숏 전체 청산
                </MenuItem>
                <MenuItem
                  bg="gray.800"
                  _hover={{ bg: 'gray.700' }}
                  onClick={() => handleCloseAllPositions('long')}
                >
                  🔵 롱 전체 청산
                </MenuItem>
                <MenuItem
                  bg="gray.800"
                  _hover={{ bg: 'red.700' }}
                  color="red.300"
                  onClick={() => handleCloseAllPositions('all')}
                >
                  ⚠️ 모두 청산
                </MenuItem>
              </MenuList>
            </Menu>
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                colorScheme="teal"
                rightIcon={<ChevronDownIcon />}
              >
                봇 상태 관리
              </MenuButton>
              <MenuList bg="gray.800" borderColor="gray.600">
                <MenuItem
                  bg="gray.800"
                  _hover={{ bg: 'gray.700' }}
                  color="green.400"
                  onClick={() => handleToggleAllBotStatus(true)}
                >
                  🟢 전체 ON
                </MenuItem>
                <MenuItem
                  bg="gray.800"
                  _hover={{ bg: 'gray.700' }}
                  color="red.400"
                  onClick={() => handleToggleAllBotStatus(false)}
                >
                  🔴 전체 OFF
                </MenuItem>
              </MenuList>
            </Menu>
            <Select
              width={{ base: '110px', md: '120px' }}
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                setPage(1)
              }}
              bg="dark.bg"
              borderColor="whiteAlpha.300"
              color="white"
              _hover={{ borderColor: 'whiteAlpha.400' }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n} style={{ background: '#1a1a2e', color: 'white' }}>
                  {n}개씩
                </option>
              ))}
            </Select>
          </HStack>
        </Flex>

        {/* 총 건수/페이지 */}
        <Flex justify="space-between" align="center" mb={2}>
          <Text color="gray.400" fontSize="sm">
            총 <b>{total.toLocaleString()}</b>명
          </Text>
          <Text color="gray.500" fontSize="sm">
            페이지 {page} / {totalPages}
          </Text>
        </Flex>

        {/* 리스트 */}
        {loading ? (
          <Flex align="center" justify="center" py={8} gap={2}>
            <Spinner />
            <Text>불러오는 중…</Text>
          </Flex>
        ) : items.length === 0 ? (
          <Flex align="center" justify="center" py={8}>
            <Text color="gray.500">데이터가 없습니다.</Text>
          </Flex>
        ) : isMobile ? (
          <Stack spacing={3}>
            {items.map((u) => (
              <MobileCard key={u.id} u={u} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={3}>
            {items.map((u) => (
              <Box 
                key={u.id} 
                bg="gray.800" 
                borderRadius="lg" 
                p={4} 
                borderWidth="1px" 
                borderColor="gray.700"
                _hover={{ borderColor: 'gray.600' }}
              >
                {/* 1단: 모든 정보 (라벨 + 값) - 고정 너비 */}
                <Flex gap={4} wrap="wrap" align="flex-start" mb={3}>
                  <Box w="90px">
                    <Text color="gray.500" fontSize="xs">아이디</Text>
                    <Text fontSize="sm">{u.account}</Text>
                  </Box>
                  <Box w="70px">
                    <Text color="gray.500" fontSize="xs">이름</Text>
                    <Text fontWeight="bold" fontSize="sm">{u.username}</Text>
                  </Box>
                  <Box w="50px">
                    <Text color="gray.500" fontSize="xs">봇</Text>
                    {getXrpTrading(u) ? (
                      <Badge 
                        colorScheme={getXrpTrading(u)?.botStatus ? 'green' : 'red'} 
                        variant="solid"
                        fontSize="xs"
                      >
                        {getXrpTrading(u)?.botStatus ? 'ON' : 'OFF'}
                      </Badge>
                    ) : (
                      <Text fontSize="sm" color="gray.500">-</Text>
                    )}
                  </Box>
                  <Box w="70px">
                    <Text color="gray.500" fontSize="xs">시드</Text>
                    <Text fontSize="sm" fontWeight="bold" color="orange.400">
                      {getXrpTrading(u)?.seedAmount ? `$${getXrpTrading(u)?.seedAmount}` : '-'}
                    </Text>
                  </Box>
                  <Box w="50px">
                    <Text color="gray.500" fontSize="xs">전략</Text>
                    <Text fontSize="sm" fontWeight="bold" color="blue.400">
                      {getXrpTrading(u)?.strategyRatio || '-'}
                    </Text>
                  </Box>
                  <Box w="55px">
                    <Text color="gray.500" fontSize="xs">권한</Text>
                    {Boolean(u.isAdmin) ? (
                      <Badge colorScheme="cyan" fontSize="xs">관리자</Badge>
                    ) : (
                      <Badge colorScheme="gray" fontSize="xs">일반</Badge>
                    )}
                  </Box>
                  <Box w="100px">
                    <Text color="gray.500" fontSize="xs">연락처</Text>
                    <Text fontSize="sm">{u.phone ?? '-'}</Text>
                  </Box>
                  <Box w="70px">
                    <Text color="gray.500" fontSize="xs">USDT</Text>
                    <Text fontWeight="bold" color="green.400" fontSize="sm">{formatUSDTInt(u.usdtBalance)}</Text>
                  </Box>
                  <Box w="70px">
                    <Text color="gray.500" fontSize="xs">포인트</Text>
                    <Text fontWeight="bold" color="blue.400" fontSize="sm">{formatUSDTInt(u.pointBalance)}</Text>
                  </Box>
                  <Box w="150px">
                    <Text color="gray.500" fontSize="xs">서비스일</Text>
                    <Text fontSize="sm"><ExpiryCell value={u.serviceAt} /></Text>
                  </Box>
                  <Box w="180px">
                    <Text color="gray.500" fontSize="xs">봇 상태</Text>
                    <Box>
                      {(() => {
                        const trading = u.trading?.find(t => t.symbol === "XRP") || u.trading?.[0]
                        if(!trading) return <Text fontSize="sm" color="gray.500">-</Text>
                        return (
                          <HStack spacing={2} mt="2px">
                            <Badge colorScheme="purple" variant="subtle" fontSize="xs">{trading.size}</Badge>
                            <Badge colorScheme="blue" variant="outline" fontSize="xs">L:{trading.currentAddOrderLong}</Badge>
                            <Badge colorScheme="red" variant="outline" fontSize="xs">S:{trading.currentAddOrderShort}</Badge>
                          </HStack>
                        )
                      })()}
                    </Box>
                  </Box>
                  <Box w="150px">
                    <Text color="gray.500" fontSize="xs">레퍼럴</Text>
                    <Text fontSize="sm"><ExpiryCell value={u.referralAt} /></Text>
                  </Box>
                  <Box w="150px">
                    <Text color="gray.500" fontSize="xs">구독</Text>
                    <Text fontSize="sm"><ExpiryCell value={u.subscriptionAt} /></Text>
                  </Box>
                  <Box w="150px">
                    <Text color="gray.500" fontSize="xs">생성일</Text>
                    <Text fontSize="sm">{formatDate(u.createdAt)}</Text>
                  </Box>
                  {u.adminMemo && (
                    <Box w="auto" maxW="200px">
                      <Text color="gray.500" fontSize="xs">메모</Text>
                      <Text fontSize="xs" color="yellow.400" bg="yellow.900" px={2} py={0.5} borderRadius="md" noOfLines={1}>
                        📝 {u.adminMemo}
                      </Text>
                    </Box>
                  )}
                </Flex>

                {/* 2단: 액션 버튼들 */}
                <Flex gap={2} wrap="wrap">
                  <Button size="xs" variant="outline" onClick={() => openAdjustModal(u)}>USDT 조정</Button>
                  <Button size="xs" variant="outline" colorScheme="blue" onClick={() => openAdjustPointModal(u)}>포인트 조정</Button>
                  <Button size="xs" variant="outline" colorScheme="cyan" onClick={() => openChangeUsernameModal(u)}>이름변경</Button>
                  <Button size="xs" variant="outline" colorScheme="red" onClick={() => openChangePasswordModal(u)}>비번변경</Button>
                  <Button size="xs" colorScheme="green" onClick={() => openPlanModal(u)}>이용권</Button>
                  <Button size="xs" colorScheme="teal" onClick={() => openIncreaseModal(u)}>이용일+</Button>
                  <Button size="xs" colorScheme="purple" onClick={() => openSetDateModal(u)}>날짜조정</Button>
                  <Button size="xs" colorScheme="orange" onClick={() => openApiKeyModal(u)}>API</Button>
                  <Button size="xs" colorScheme="teal" onClick={() => openReferrerModal(u)}>추천인</Button>
                  <Button size="xs" colorScheme={u.isAdmin ? 'red' : 'cyan'} variant={u.isAdmin ? 'outline' : 'solid'} onClick={() => handleToggleAdmin(u)}>
                    {u.isAdmin ? '관리자해제' : '관리자부여'}
                  </Button>
                  <Button size="xs" colorScheme="pink" onClick={() => openPositionModal(u)}>청산</Button>
                  <Button size="xs" colorScheme="cyan" onClick={() => openMarginModeModal(u)}>마진</Button>
                  <Button size="xs" colorScheme="purple" onClick={() => openDcaResetModal(u)}>DCA초기화</Button>
                  <Button size="xs" colorScheme={getXrpTrading(u)?.botStatus ? 'red' : 'green'} variant="solid" onClick={() => handleToggleBotStatus(u, !getXrpTrading(u)?.botStatus)}>
                    {getXrpTrading(u)?.botStatus ? '봇OFF' : '봇ON'}
                  </Button>
                  {!u.isAdmin && (
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleDeleteUser(u)}>삭제</Button>
                  )}
                  <Button size="xs" colorScheme="yellow" variant="outline" onClick={() => openMemoModal(u)}>메모</Button>
                </Flex>
              </Box>
            ))}
          </Stack>
        )}

        {/* 페이지네이션 */}
        <Flex align="center" justify="center" gap={2} mt={4}>
          <Button
            onClick={() => setPage(1)}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            처음
          </Button>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            이전
          </Button>
          <Text fontSize="sm">
            {page} / {totalPages}
          </Text>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            다음
          </Button>
          <Button
            onClick={() => setPage(totalPages)}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            마지막
          </Button>
        </Flex>
      </Box>

      {/* USDT 조정 모달 */}
      <Modal isOpen={adjustDisc.isOpen} onClose={adjustDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>USDT 지급/회수</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 지급/회수 선택 */}
            <HStack mb={3}>
              <ButtonGroup isAttached>
                <Button
                  onClick={() => setAdjustType('credit')}
                  colorScheme={adjustType === 'credit' ? 'blue' : undefined}
                >
                  지급
                </Button>
                <Button
                  onClick={() => setAdjustType('debit')}
                  colorScheme={adjustType === 'debit' ? 'red' : undefined}
                >
                  회수
                </Button>
              </ButtonGroup>
            </HStack>

            {/* ✅ 퀵 버튼 */}
            <HStack mb={3} spacing={2}>
              {[200, 2400, 4000, 5000].map((amt) => (
                <Button
                  key={amt}
                  size="sm"
                  variant="outline"
                  onClick={() => setAdjustAmount(String(amt))}
                >
                  {amt.toLocaleString()}
                </Button>
              ))}
            </HStack>

            {/* 금액 입력 */}
            <NumberInput
              value={adjustAmount}
              onChange={(v) => setAdjustAmount(v)}
              min={0}
            >
              <NumberInputField placeholder="금액 입력 (정수/소수 가능)" />
            </NumberInput>

            <Text mt={2} fontSize="xs" color="gray.500">
              지급은 +, 회수는 -로 처리됩니다. (회수 시 음수로 변환)
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={adjustDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingAdjust}
              onClick={submitAdjust}
              colorScheme={adjustType === 'credit' ? 'blue' : 'red'}
            >
              적용
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 이용권 구매 모달 */}
      <Modal isOpen={planDisc.isOpen} onClose={planDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이용권 구매</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>
            <Select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value as PlanValue)}
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>

            <Box mt={3}>
              <Text fontSize={'sm'}>
                사용자의 USDT를 차감하고 이용권을 구매합니다.
              </Text>
              <Text fontSize={'sm'}>
                따라서, 오프라인 매출의 경우 USDT 지급 후 해당 기능을
                사용해야합니다.
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={planDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingPlan}
              onClick={submitPlan}
              colorScheme="blue"
            >
              구매
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 이용일 증가 모달 */}
      <Modal
        isOpen={increaseDisc.isOpen}
        onClose={increaseDisc.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이용일 증가 (관리자)</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 퀵 버튼 */}
            <HStack mb={3} spacing={2}>
              {[1, 3, 7, 30, 90, 365].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant="outline"
                  onClick={() => setIncreaseDays(String(d))}
                >
                  +{d}일
                </Button>
              ))}
            </HStack>

            {/* 일수 입력 */}
            <NumberInput
              value={increaseDays}
              onChange={(v) => setIncreaseDays(v)}
              min={1}
            >
              <NumberInputField placeholder="증가 일수 (정수)" />
            </NumberInput>

            {/* ✅ 연장 후 예상 만료일 표시 */}
            {nextExpiryHint && (
              <Text mt={3} fontSize="md" fontWeight="bold" color="blue.600">
                {nextExpiryHint}
              </Text>
            )}

            <Text mt={2} fontSize="xs" color="gray.500">
              만료된 경우 오늘 기준으로, 유효한 경우 기존 만료일 기준으로
              연장됩니다.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={increaseDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingIncrease}
              onClick={submitIncrease}
              colorScheme="teal"
            >
              적용
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 포인트 조정 모달 */}
      <Modal isOpen={adjustPointDisc.isOpen} onClose={adjustPointDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>포인트 지급/회수</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 지급/회수 선택 */}
            <HStack mb={3}>
              <ButtonGroup isAttached>
                <Button
                  onClick={() => setAdjustPointType('credit')}
                  colorScheme={adjustPointType === 'credit' ? 'blue' : undefined}
                >
                  지급
                </Button>
                <Button
                  onClick={() => setAdjustPointType('debit')}
                  colorScheme={adjustPointType === 'debit' ? 'red' : undefined}
                >
                  회수
                </Button>
              </ButtonGroup>
            </HStack>

            {/* 퀵 버튼 */}
            <HStack mb={3} spacing={2}>
              {[100, 500, 1000, 5000].map((amt) => (
                <Button
                  key={amt}
                  size="sm"
                  variant="outline"
                  onClick={() => setAdjustPointAmount(String(amt))}
                >
                  {amt.toLocaleString()}
                </Button>
              ))}
            </HStack>

            {/* 포인트 입력 */}
            <NumberInput
              value={adjustPointAmount}
              onChange={(v) => setAdjustPointAmount(v)}
              min={0}
            >
              <NumberInputField placeholder="포인트 입력 (정수/소수 가능)" />
            </NumberInput>

            <Text mt={2} fontSize="xs" color="gray.500">
              지급은 +, 회수는 -로 처리됩니다. (회수 시 음수로 변환)
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={adjustPointDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingAdjustPoint}
              onClick={submitAdjustPoint}
              colorScheme={adjustPointType === 'credit' ? 'blue' : 'red'}
            >
              적용
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 사용자명 변경 모달 */}
      <Modal isOpen={changeUsernameDisc.isOpen} onClose={changeUsernameDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>사용자명 변경</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 새 사용자명 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                새 사용자명
              </FormLabel>
              <Input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="새 사용자명을 입력하세요"
                size="sm"
              />
            </FormControl>

            <Text mt={2} fontSize="xs" color="gray.500">
              사용자명을 변경하면 즉시 반영됩니다.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={changeUsernameDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingChangeUsername}
              onClick={submitChangeUsername}
              colorScheme="cyan"
            >
              변경
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 비밀번호 변경 모달 */}
      <Modal isOpen={changePasswordDisc.isOpen} onClose={changePasswordDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>비밀번호 변경</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 새 비밀번호 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                새 비밀번호
              </FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="새 비밀번호를 입력하세요 (4글자 이상)"
                size="sm"
              />
            </FormControl>

            {/* 비밀키 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                비밀키
              </FormLabel>
              <Input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="비밀키를 입력하세요"
                size="sm"
              />
            </FormControl>

            <Text mt={2} fontSize="xs" color="gray.500">
              비밀번호는 4글자 이상이어야 하며, 비밀키는 관리자만 알고 있는 키입니다.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={changePasswordDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingChangePassword}
              onClick={submitChangePassword}
              colorScheme="red"
            >
              변경
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 날짜 조정 모달 */}
      <Modal isOpen={setDateDisc.isOpen} onClose={setDateDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>날짜 조정</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 날짜 유형 선택 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                조정할 날짜 유형
              </FormLabel>
              <Select
                size="sm"
                value={dateType}
                onChange={(e) => setDateType(e.target.value as any)}
              >
                <option value="serviceAt">서비스일</option>
                <option value="referralAt">레퍼럴일</option>
                <option value="subscriptionAt">구독료일</option>
              </Select>
            </FormControl>

            {/* 현재 날짜 표시 */}
            {selectedUser && (
              <Text fontSize="xs" color="gray.500" mb={2}>
                현재 값:{' '}
                {dateType === 'serviceAt' && (selectedUser.serviceAt 
                  ? dayjs(selectedUser.serviceAt).format('YYYY-MM-DD HH:mm:ss') 
                  : '미설정')}
                {dateType === 'referralAt' && (selectedUser.referralAt 
                  ? dayjs(selectedUser.referralAt).format('YYYY-MM-DD HH:mm:ss') 
                  : '미설정')}
                {dateType === 'subscriptionAt' && (selectedUser.subscriptionAt 
                  ? dayjs(selectedUser.subscriptionAt).format('YYYY-MM-DD HH:mm:ss') 
                  : '미설정')}
              </Text>
            )}

            {/* 새 날짜 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                새 날짜
              </FormLabel>
              <Input
                type="text"
                value={newDateValue}
                onChange={(e) => setNewDateValue(e.target.value)}
                placeholder="yyyy-mm-dd hh:mm:ss"
                size="sm"
              />
            </FormControl>

            <Text mt={2} fontSize="xs" color="gray.500">
              선택한 날짜로 해당 유형의 만료일이 직접 설정됩니다.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={setDateDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingSetDate}
              onClick={submitSetDate}
              colorScheme="purple"
            >
              조정
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* API 키 설정 모달 */}
      <Modal isOpen={apiKeyDisc.isOpen} onClose={apiKeyDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>API 키 설정</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* API Key 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                Gate.io API Key
              </FormLabel>
              <Input
                type="text"
                value={apiKeyValue}
                onChange={(e) => setApiKeyValue(e.target.value)}
                placeholder="API Key를 입력하세요"
                size="sm"
              />
            </FormControl>

            {/* API Secret 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                Gate.io API Secret
              </FormLabel>
              <InputGroup size="sm">
                <Input
                  type={showApiSecret ? 'text' : 'password'}
                  value={apiSecretValue}
                  onChange={(e) => setApiSecretValue(e.target.value)}
                  placeholder="API Secret을 입력하세요"
                  pr="3rem"
                />
                <InputRightElement width="3rem">
                  <IconButton
                    aria-label={showApiSecret ? '숨기기' : '보이기'}
                    icon={showApiSecret ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    color="gray.300"
                    _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                    onClick={() => setShowApiSecret(!showApiSecret)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Text mt={2} fontSize="xs" color="gray.500">
              Gate.io 거래소에서 발급받은 API 키와 시크릿을 입력하세요.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={apiKeyDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingApiKey}
              onClick={submitApiKey}
              colorScheme="orange"
            >
              저장
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 포지션 청산 모달 */}
      <Modal isOpen={positionDisc.isOpen} onClose={positionDisc.onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Text>포지션 청산</Text>
              <Button 
                size="sm" 
                variant="outline" 
                colorScheme="cyan"
                onClick={() => selectedUser && openPositionModal(selectedUser)}
                isLoading={loadingPosition}
              >
                새로고침
              </Button>
            </Flex>
          </ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.400" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {loadingPosition ? (
              <Flex justify="center" py={8}>
                <Spinner />
                <Text ml={3}>포지션 조회 중...</Text>
              </Flex>
            ) : positionData ? (
              <Box>
                <Text fontSize="sm" color="gray.500" mb={2}>심볼: {positionData.symbol} / {positionData.contract}</Text>
                
                {!positionData.hasPosition ? (
                  <Box p={4} bg="gray.700" borderRadius="md">
                    <Text textAlign="center" mb={3}>현재 보유 중인 포지션이 없습니다.</Text>
                    <Divider my={3} />
                    <Text fontSize="sm" color="gray.400" mb={2}>물타기 횟수 초기화</Text>
                    <Flex gap={2} wrap="wrap">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        isLoading={resettingDCA}
                        onClick={() => handleResetDCA('long')}
                      >
                        롱 초기화 ({positionData.currentAddOrderLong || 0}회)
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        isLoading={resettingDCA}
                        onClick={() => handleResetDCA('short')}
                      >
                        숏 초기화 ({positionData.currentAddOrderShort || 0}회)
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="yellow"
                        variant="outline"
                        isLoading={resettingDCA}
                        onClick={() => handleResetDCA('all')}
                      >
                        전체 초기화
                      </Button>
                    </Flex>
                  </Box>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {/* 사이즈 설명 */}
                    <Text fontSize="xs" color="gray.500">
                      ※ 사이즈 단위: 1 계약 = 10 XRP
                    </Text>
                    
                    {/* 롱 포지션 */}
                    {positionData.long && (
                      <Box p={3} bg="blue.900" borderRadius="md">
                        <Flex justify="space-between" align="center" mb={2}>
                          <HStack>
                            <Badge colorScheme="blue" fontSize="sm">롱 포지션</Badge>
                            <Badge colorScheme="gray" fontSize="xs">물타기 {positionData.currentAddOrderLong || 0}회</Badge>
                          </HStack>
                        </Flex>
                        <Stack spacing={1} fontSize="sm">
                          <Flex justify="space-between">
                            <Text color="gray.400">사이즈:</Text>
                            <Text>{positionData.long.size} 계약 (≈ {positionData.long.size * 10} XRP)</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text color="gray.400">진입가:</Text>
                            <Text>{positionData.long.entryPrice}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text color="gray.400">수익률:</Text>
                            <Text color={parseFloat(positionData.long.roi) >= 0 ? 'green.400' : 'red.400'} fontWeight="bold">
                              {parseFloat(positionData.long.roi) >= 0 ? '+' : ''}{positionData.long.roi}%
                            </Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text color="gray.400">미실현 손익:</Text>
                            <Text color={parseFloat(positionData.long.unrealisedPnl) >= 0 ? 'green.400' : 'red.400'}>
                              {positionData.long.unrealisedPnl} USDT
                            </Text>
                          </Flex>
                        </Stack>
                        {/* 청산 사이즈 입력 */}
                        <Box mt={3} pt={3} borderTop="1px solid" borderColor="blue.700">
                          <Text fontSize="xs" color="gray.400" mb={2}>청산 사이즈 (계약)</Text>
                          <Flex gap={2}>
                            <NumberInput
                              size="sm"
                              min={1}
                              max={positionData.long.size}
                              value={closeSizeLong}
                              onChange={(val) => setCloseSizeLong(val)}
                              flex={1}
                            >
                              <NumberInputField placeholder={`1~${positionData.long.size}`} />
                            </NumberInput>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              isLoading={submittingClosePosition}
                              onClick={() => submitClosePosition('long', closeSizeLong ? parseInt(closeSizeLong) : undefined)}
                              isDisabled={!closeSizeLong || parseInt(closeSizeLong) <= 0}
                            >
                              부분 청산
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              isLoading={submittingClosePosition}
                              onClick={() => submitClosePosition('long')}
                            >
                              전체 청산
                            </Button>
                          </Flex>
                        </Box>
                      </Box>
                    )}

                    {/* 숏 포지션 */}
                    {positionData.short && (
                      <Box p={3} bg="red.900" borderRadius="md">
                        <Flex justify="space-between" align="center" mb={2}>
                          <HStack>
                            <Badge colorScheme="red" fontSize="sm">숏 포지션</Badge>
                            <Badge colorScheme="gray" fontSize="xs">물타기 {positionData.currentAddOrderShort || 0}회</Badge>
                          </HStack>
                        </Flex>
                        <Stack spacing={1} fontSize="sm">
                          <Flex justify="space-between">
                            <Text color="gray.400">사이즈:</Text>
                            <Text>{positionData.short.size} 계약 (≈ {positionData.short.size * 10} XRP)</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text color="gray.400">진입가:</Text>
                            <Text>{positionData.short.entryPrice}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text color="gray.400">수익률:</Text>
                            <Text color={parseFloat(positionData.short.roi) >= 0 ? 'green.400' : 'red.400'} fontWeight="bold">
                              {parseFloat(positionData.short.roi) >= 0 ? '+' : ''}{positionData.short.roi}%
                            </Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text color="gray.400">미실현 손익:</Text>
                            <Text color={parseFloat(positionData.short.unrealisedPnl) >= 0 ? 'green.400' : 'red.400'}>
                              {positionData.short.unrealisedPnl} USDT
                            </Text>
                          </Flex>
                        </Stack>
                        {/* 청산 사이즈 입력 */}
                        <Box mt={3} pt={3} borderTop="1px solid" borderColor="red.700">
                          <Text fontSize="xs" color="gray.400" mb={2}>청산 사이즈 (계약)</Text>
                          <Flex gap={2}>
                            <NumberInput
                              size="sm"
                              min={1}
                              max={positionData.short.size}
                              value={closeSizeShort}
                              onChange={(val) => setCloseSizeShort(val)}
                              flex={1}
                            >
                              <NumberInputField placeholder={`1~${positionData.short.size}`} />
                            </NumberInput>
                            <Button
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              isLoading={submittingClosePosition}
                              onClick={() => submitClosePosition('short', closeSizeShort ? parseInt(closeSizeShort) : undefined)}
                              isDisabled={!closeSizeShort || parseInt(closeSizeShort) <= 0}
                            >
                              부분 청산
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="red"
                              isLoading={submittingClosePosition}
                              onClick={() => submitClosePosition('short')}
                            >
                              전체 청산
                            </Button>
                          </Flex>
                        </Box>
                      </Box>
                    )}

                    {/* 전체 청산 버튼 */}
                    {positionData.long && positionData.short && (
                      <Button
                        colorScheme="pink"
                        isLoading={submittingClosePosition}
                        onClick={() => submitClosePosition('all')}
                        mt={2}
                      >
                        전체 청산
                      </Button>
                    )}
                  </VStack>
                )}
              </Box>
            ) : (
              <Box p={4} bg="gray.700" borderRadius="md" textAlign="center">
                <Text color="red.400">포지션 정보를 조회할 수 없습니다.</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>사용자의 API 키가 설정되어 있는지 확인하세요.</Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="blue" 
              onClick={positionDisc.onClose}
              size="md"
            >
              닫기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 마진 모드 변경 모달 */}
      <Modal isOpen={marginModeDisc.isOpen} onClose={marginModeDisc.onClose} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader borderBottom="1px solid" borderColor="gray.700">
            <HStack>
              <Text>마진 모드 변경</Text>
              <Badge colorScheme="teal">{selectedUser?.account}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <Text fontSize="sm" color="gray.400" mb={4}>
              심볼: XRP_USDT
            </Text>

            {loadingMarginCheck ? (
              <Flex justify="center" py={8}>
                <Spinner />
                <Text ml={3}>포지션 확인 중...</Text>
              </Flex>
            ) : canChangeMarginMode ? (
              <Box>
                <Box p={4} bg="green.900" borderRadius="md" mb={4}>
                  <Text color="green.300" fontWeight="bold">✓ 포지션 없음 - 마진 모드 변경 가능</Text>
                </Box>
                <VStack spacing={3}>
                  <Button 
                    colorScheme="teal" 
                    size="lg" 
                    w="100%" 
                    isLoading={submittingMarginMode}
                    onClick={() => submitSwitchMarginMode('CROSS')}
                  >
                    교차(Cross) 마진으로 변경
                  </Button>
                  <Button 
                    colorScheme="orange" 
                    size="lg" 
                    w="100%" 
                    isLoading={submittingMarginMode}
                    onClick={() => submitSwitchMarginMode('ISOLATED')}
                  >
                    격리(Isolated) 마진으로 변경
                  </Button>
                </VStack>
                <Text fontSize="xs" color="gray.500" mt={4}>
                  ※ Cross: 잔고 전체를 마진으로 사용 / Isolated: 포지션별 마진 분리
                </Text>
              </Box>
            ) : (
              <Box p={4} bg="red.900" borderRadius="md">
                <Text color="red.300" fontWeight="bold">✗ 포지션 보유 중 - 마진 모드 변경 불가</Text>
                <Text fontSize="sm" color="gray.400" mt={2}>
                  마진 모드를 변경하려면 먼저 모든 포지션을 청산해주세요.
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={marginModeDisc.onClose}>
              닫기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 추천인 설정 모달 */}
      <Modal isOpen={referrerDisc.isOpen} onClose={referrerDisc.onClose} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader borderBottom="1px solid" borderColor="gray.700">
            <HStack>
              <Text>추천인 설정</Text>
              <Badge colorScheme="teal">{selectedUser?.account}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl>
              <FormLabel>추천인 아이디</FormLabel>
              <Input
                placeholder="추천인 아이디를 입력하세요 (빈 값이면 삭제)"
                value={referrerAccount}
                onChange={(e) => setReferrerAccount(e.target.value)}
                bg="gray.700"
                borderColor="gray.600"
                _hover={{ borderColor: 'gray.500' }}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
              />
              <Text fontSize="xs" color="gray.500" mt={2}>
                ※ 빈 값으로 저장하면 추천인이 삭제됩니다.
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={referrerDisc.onClose}
              >
                취소
              </Button>
              <Button
                colorScheme="blue"
                onClick={submitReferrer}
                isLoading={submittingReferrer}
              >
                저장
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 회원 추가 모달 */}
      <Modal isOpen={createUserDisc.isOpen} onClose={createUserDisc.onClose} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader borderBottom="1px solid" borderColor="gray.700">
            회원 추가
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>아이디</FormLabel>
                <Input
                  placeholder="아이디를 입력하세요"
                  value={newUserAccount}
                  onChange={(e) => setNewUserAccount(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>비밀번호</FormLabel>
                <Input
                  type="password"
                  placeholder="비밀번호를 입력하세요"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>사용자명</FormLabel>
                <Input
                  placeholder="사용자명을 입력하세요"
                  value={newUserUsername}
                  onChange={(e) => setNewUserUsername(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>연락처</FormLabel>
                <Input
                  placeholder="010-0000-0000"
                  value={newUserPhone}
                  onChange={(e) => setNewUserPhone(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                />
              </FormControl>
              <FormControl>
                <FormLabel>추천인 (선택)</FormLabel>
                <Input
                  placeholder="추천인 아이디"
                  value={newUserReferrer}
                  onChange={(e) => setNewUserReferrer(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={createUserDisc.onClose}
              >
                취소
              </Button>
              <Button
                colorScheme="green"
                onClick={submitCreateUser}
                isLoading={submittingCreateUser}
              >
                추가
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 관리자 메모 모달 */}
      <Modal isOpen={memoDisc.isOpen} onClose={memoDisc.onClose} size="lg">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader borderBottom="1px solid" borderColor="gray.700">
            <HStack>
              <Text>관리자 메모</Text>
              <Badge colorScheme="teal">{selectedUser?.account}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl>
              <FormLabel>메모 내용</FormLabel>
              <Textarea
                placeholder="회원에 대한 메모를 입력하세요..."
                value={memoContent}
                onChange={(e) => setMemoContent(e.target.value)}
                bg="gray.700"
                borderColor="gray.600"
                rows={6}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
              />
              <Text fontSize="xs" color="gray.500" mt={2}>
                ※ 이 메모는 관리자만 볼 수 있습니다.
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={memoDisc.onClose}
              >
                취소
              </Button>
              <Button
                colorScheme="yellow"
                onClick={submitMemo}
                isLoading={submittingMemo}
              >
                저장
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* DCA 초기화 모달 */}
      <Modal isOpen={dcaResetDisc.isOpen} onClose={dcaResetDisc.onClose} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader borderBottom="1px solid" borderColor="gray.700">
            <HStack>
              <Text>물타기 횟수 초기화</Text>
              <Badge colorScheme="teal">{selectedUser?.account}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {loadingDcaData ? (
              <Flex justify="center" py={4}>
                <Spinner />
                <Text ml={3}>조회 중...</Text>
              </Flex>
            ) : dcaResetData ? (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.400">
                  유저가 거래소에서 직접 청산한 경우, 물타기 횟수를 수동으로 초기화할 수 있습니다.
                </Text>
                
                <Box p={4} bg="gray.700" borderRadius="md">
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack>
                      <Badge colorScheme="blue">롱</Badge>
                      <Text>물타기 횟수: <b>{dcaResetData.long}회</b></Text>
                    </HStack>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      isLoading={resettingDCA}
                      onClick={() => handleDcaReset('long')}
                      isDisabled={dcaResetData.long === 0}
                    >
                      롱 초기화
                    </Button>
                  </Flex>
                  <Divider my={3} />
                  <Flex justify="space-between" align="center">
                    <HStack>
                      <Badge colorScheme="red">숏</Badge>
                      <Text>물타기 횟수: <b>{dcaResetData.short}회</b></Text>
                    </HStack>
                    <Button
                      size="sm"
                      colorScheme="red"
                      isLoading={resettingDCA}
                      onClick={() => handleDcaReset('short')}
                      isDisabled={dcaResetData.short === 0}
                    >
                      숏 초기화
                    </Button>
                  </Flex>
                </Box>
                
                <Button
                  colorScheme="yellow"
                  isLoading={resettingDCA}
                  onClick={() => handleDcaReset('all')}
                  isDisabled={dcaResetData.long === 0 && dcaResetData.short === 0}
                >
                  전체 초기화
                </Button>
              </VStack>
            ) : (
              <Text color="gray.500">데이터를 불러올 수 없습니다.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={dcaResetDisc.onClose}>
              닫기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default User
